import React, { useState } from 'react';
import { Info, X } from 'lucide-react';

const GuidelinesStep = ({ guidelinesAcknowledged, setGuidelinesAcknowledged, isMobile }) => {
  const [showTermsModal, setShowTermsModal] = useState(false);
  return (
    <div className="flex flex-col w-full h-full bg-white rounded-lg font-sans">
      {/* Header */}
      <div className="flex flex-col justify-center items-center px-3 sm:px-4 border-b border-gray-200 shrink-0 py-2">
        <div className="flex items-center">
          <Info size={20} className="text-indigo-500 mr-2" />
          <h2 className="text-sm font-semibold text-gray-800">
            Interview Guidelines
          </h2>
        </div>

        <p className="text-gray-600 text-xs">Scroll to read and accept</p>
      </div>

      {/* Scrollable content area with reduced max height */}
      <div className="flex-1 overflow-y-auto space-y-3 max-h-[40vh] md:max-h-[50vh] px-3 py-2">
        {/* Don'ts Section */}
        <div className="mb-3">
          <div className="flex items-center mb-2 bg-red-50 p-2 rounded-md">
            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-red-500 flex items-center justify-center mr-2 shrink-0">
              <span className="text-white font-bold text-[10px]">✕</span>
            </div>
            <h3 className="text-xs font-semibold text-red-700 m-0">
              Important Cautions
            </h3>
          </div>

          <ul className="pl-2 m-0 text-gray-700 text-xs leading-relaxed list-none">
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-red-500">•</span>
              <strong className="text-gray-900">Don't refresh the tab</strong> during the interview.
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-red-500">•</span>
              <strong className="text-gray-900">Don't interrupt Sarah</strong> before she finishes speaking.
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-red-500">•</span>
              <strong className="text-gray-900">Don't use vague responses</strong> in your answers.
            </li>
            <li className="pl-4 relative">
              <span className="absolute left-0 text-red-500">•</span>
              <strong className="text-gray-900">Don't proceed</strong> without saying "Yes, I am ready."
            </li>
          </ul>
        </div>

        {/* Do's Section */}
        <div>
          <div className="flex items-center mb-2 bg-green-50 p-2 rounded-md">
            <div className="w-4 h-4 sm:w-5 sm:h-5 rounded-full bg-green-500 flex items-center justify-center mr-2 shrink-0">
              <span className="text-white font-bold text-[10px]">✓</span>
            </div>
            <h3 className="text-xs font-semibold text-green-700 m-0">
              Best Practices
            </h3>
          </div>

          <ul className="pl-2 m-0 text-gray-700 text-xs leading-relaxed list-none">
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              <strong className="text-gray-900">Stick to interview topics only.</strong>
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              <strong className="text-gray-900">Use Chrome browser</strong> for best compatibility.
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              When Sarah asks <strong className="text-gray-900">"Can we start?"</strong> reply with:
              <div className="my-1 p-1 bg-gray-50 border-l-4 border-green-500 rounded text-[10px]">
                "Yes, we can start the interview."
              </div>
            </li>
            <li className="mb-2 pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              <strong className="text-gray-900">Wait 3-5 seconds</strong> after responding.
            </li>
            <li className="pl-4 relative">
              <span className="absolute left-0 text-green-500">•</span>
              After each question, say:
              <div className="my-1 p-1 bg-gray-50 border-l-4 border-green-500 rounded text-[10px]">
                "Yes, I am ready."
              </div>
            </li>
          </ul>
        </div>
      </div>
      
      <div className="px-3 py-2 border-t border-gray-200 bg-white shrink-0">
        <div className="flex items-start">
          <input
            type="checkbox"
            id="guidelinesAcknowledged"
            className="mr-2 mt-1 w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500"
            checked={guidelinesAcknowledged}
            onChange={(e) => setGuidelinesAcknowledged(e.target.checked)}
          />
          <label
            htmlFor="guidelinesAcknowledged"
            className="text-[10px] sm:text-xs text-gray-700 leading-tight cursor-pointer"
          >
            I have read and understand the interview guidelines and agree to the{' '}
            <button 
              type="button"
              className="text-indigo-600 underline focus:outline-none"
              onClick={() => setShowTermsModal(true)}
            >
              Terms and Conditions
            </button>{' '}
            and will follow them during my interview.
          </label>
        </div>
      </div>
      
      {/* Terms and Conditions Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-md max-h-[80vh] flex flex-col">
            <div className="flex justify-between items-center border-b border-gray-200 px-4 py-3">
              <h3 className="font-semibold text-gray-800">Terms and Conditions</h3>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto flex-1">
              <div className="space-y-4 text-sm text-gray-700">
                <h2 className="font-bold text-lg text-center">Welcome to PromptHire</h2>
                
                <p>
                  Welcome to PromptHire (referred to as 'PromptHire', 'we', 'our', or 'us'). These Terms and Conditions
                  ('Terms') govern your use of our website, platform, products, and services (collectively, the
                  'Services').
                </p>
                
                <p>
                  By accessing or using PromptHire, you agree to be bound by these Terms. If you do not agree,
                  please do not use our Services.
                </p>
                
                <p>
                  PromptHire is committed to creating a transparent and efficient hiring process using the latest
                  advancements in artificial intelligence, machine learning, and process automation. We connect
                  candidates and employers through a secure and structured interview ecosystem.
                </p>
              </div>
            </div>
            
            <div className="border-t border-gray-200 px-4 py-3 flex justify-end">
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuidelinesStep;